import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import DetailCard from "components/appointment/view/DetailCard";
import AppointmentViewHeader from "components/appointment/view/AppointmentViewHeader";
import ActionBar from "components/appointment/view/ActionBar";
import { appointmentRows, otherDetailRows, locationRows, requesterRows, requirementRows, dynamicFieldRows, checkInOutRows, workOrderRows, feedbackRows, videoRows } from "components/appointment/view/sections";
import { useDispatch, useSelector } from "react-redux";
import LogsTable from "./LogsTable";
import FullPageLoader from "components/common/FullPageLoader";
import { fetchInterpreterAppointmentView } from "slices/interpreter/appointmentSlice";
import { fetchInterpreterAppointmentLogs } from "slices/interpreter/appointmentSlice";
import { setActionAssign } from "slices/actionButtonSlice";
import moment from "moment-timezone";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import CancelModal from "components/appointment/CancelModal";
import { closeAppointmentPopup } from "slices/appointmentSlice";
import { CheckinCheckoutAdjustmentModal } from "components/appointment/checkinCheckoutAdjustment";
import RecordingModal from "views/JoinRoomCall/RecordingModal";
import { Video } from "lucide-react";
import { setInterpreterIframeUrl } from "slices/interpreter/interpreterIframeSlice";
import Api from "services/api";
import ConfirmationModal from "components/common/ConfirmationMessage";
import ConfirmationInvitesModal from "components/common/ConfirmationInvites";
import { answerAppointmentInvite } from "slices/interpreter/appointmentSlice";
import { getCheckInStatus } from "slices/userSlice";
import { userSelector } from "slices/userSlice";
import Swal from 'sweetalert2';
import { useTranslation } from "react-i18next";

const AppointmentView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector(userSelector);

  const [item, setItem] = React.useState({});
  const [loading, setLoading] = React.useState(false);
  const [logs, setLogs] = React.useState([]);
  const [checkinCheckoutModal, setCheckinCheckoutModal] = React.useState(false);
  const [cancelModal, setCancelModal] = React.useState(false);
  const [answerInviteModal, setAnswerInviteModal] = React.useState('');
  const [answerProcessing, setAnswerProcessing] = React.useState(false);
  const callViewApi = async () => {
    const callBack = (success, message, data, status) => {
      setLoading(false);
      if (success) {
        if (data?.invite_details?.status === "rejected") {
          toast.error(t("appointments.alreadyRejected"), {
            className: "errorToast",
          });
          navigate("/interpreter/dashboard");
          return;
        }
        if (data?.invite_details?.status === "cancelled") {
          navigate("/interpreter/dashboard");
          return;
        }
        setItem(data);
      } else {
        toast.error(message || t("appointments.notAuthorized"), {
          className: "errorToast",
        });
        navigate("/interpreter/dashboard");
      }
    };

    dispatch(fetchInterpreterAppointmentView(id, callBack));
  };

  const fetchLogs = async () => {
    const callBack = (success, message, data) => {
      if (success) {
        setLogs(data);
        console.log(data);
      } else {
        console.log(message);
      }
      // setLogs()
    };
    dispatch(fetchInterpreterAppointmentLogs(id, callBack));
  };

  React.useEffect(() => {
    setLoading(true);
    callViewApi();
    fetchLogs();
  }, [id]);

  const handleAnswerInvite = (answer) => {
    setLoading(true);
    setAnswerProcessing(true);
    const submitCallback = (success, message) => {
      setAnswerProcessing(false);
      if (success) {
        toast.success(message, {
          className: "successToast",
        });
        console.log(answer,'answersss');
        if(answer === 'accept'){
          const isAutoAssign = item?.appointment_details?.auto_assign === 1 && item?.invite_details?.interpreter?.auto_assign === 1
          Swal.fire({
              title: isAutoAssign ? t("appointments.assignedTitle") : t("appointments.thanksForResponding"),
              text: isAutoAssign
                  ? t("appointments.autoAssignSuccessText")
                  : t("appointments.manualAssignNoticeText"),
              icon: "success",
          });
        }
        if (answer === 'decline') {
          navigate(`/interpreter/dashboard`);
          setLoading(false);
          return;
        }
        setAnswerInviteModal('');
        callViewApi();
        fetchLogs();
        setLoading(false);
      } else {
        toast.error(message, { className: "errorToast" });
        setLoading(false);
      }
    };
    dispatch(answerAppointmentInvite(item?.id, { answer: answer }, submitCallback))
  };

  const handleOpenCheckinCheckoutModal = () => {
    setCheckinCheckoutModal(true);
  };

  const handleCancel = () => {
    setCancelModal(true);
  };

  const handleCloseCancel = () => {
    setCancelModal(false);
  };

  const handleCloseCheckinCheckoutModal = () => {
    setCheckinCheckoutModal(false);
  };

  const handleAccept = () => {
    const data = { status: "accepted" };
    handleSubmit(data, "accept", callViewApi);
  };
  const handleSubmit = (data, end_point, callBack) => {
    setLoading(true);
    const submitCallback = (success, message) => {
      callBack && callBack();
      if (success) {
        handleCloseCancel();
        handleCloseCheckinCheckoutModal();
        callViewApi();
        fetchLogs();
        toast.success(message, {
          className: "successToast",
        });
        setLoading(false);
        // scheduleDetailModal?.show && openModal()
      } else {
        toast.error(message, {
          className: "errorToast",
        });
        setLoading(false);
      }
    };
    dispatch(setActionAssign(id, data, end_point, submitCallback));
  };

  const InterpreterTimezone = item?.interpreter?.timezone || user?.timezone;
  const AppointmentTimezone = item?.appointment_details?.timezone;
  const rawDatetime = item?.datetime;

  // Date: use appointment timezone so late-night appointments don't shift the date
  const formattedDate = AppointmentTimezone?.name
    ? moment.tz(rawDatetime, AppointmentTimezone.name).format("MMM D, YYYY")
    : moment(rawDatetime).format("MMM D, YYYY");

  // Time in interpreter's timezone: convert from appointment tz → interpreter tz
  const formattedClientTime = (() => {
    if (!rawDatetime) return t("common.nA");
    let m;
    if (AppointmentTimezone?.name && InterpreterTimezone?.name) {
      m = moment.tz(rawDatetime, AppointmentTimezone.name).tz(InterpreterTimezone.name);
    } else if (InterpreterTimezone?.name) {
      m = moment.utc(rawDatetime).tz(InterpreterTimezone.name);
    } else {
      return moment(rawDatetime).format("hh:mm A");
    }
    // const abbr = InterpreterTimezone?.abbreviation;
    // const offset = InterpreterTimezone?.utc_offset;
    // const zone = abbr ? `(${abbr} / GMT${offset})` : `(GMT${offset})`;
    const dynamicAbbr = m.format("z");
    const dynamicOffset = m.format("Z");
    
    const zone = dynamicAbbr ? `(${dynamicAbbr} / GMT${dynamicOffset})` : `(GMT${dynamicOffset})`;
    return `${m.format("hh:mm A")} ${zone}`;
  })();

  const [recordingUrl, setRecordingUrl] = React.useState(null);
  const [showModal, setShowModal] = React.useState(false);


  const [checkinStatus, setCheckinStatus] = useState(0);
  const [checkInternalInterpreterStatus, setCheckcheckInternalInterpreterStatus] = useState(0);

  useEffect(() => {
    dispatch(
      getCheckInStatus((success, data) => {
        if (success) {
          setCheckinStatus(data?.data?.user?.checked_in);
          setCheckcheckInternalInterpreterStatus(data?.data?.user?.internal_interpreter);
        } else {
          console.log("Failed to fetch check-in status", data);
        }
      })
    );
  }, [dispatch]);

  //   Join Video Call handler for interpreter
  const handleInterpreterJoinVideoCall = async () => {
    if (checkinStatus !== 1 && checkInternalInterpreterStatus === 1) {
      toast.error(t("appointments.checkinFirstBeforeVideo"), {
        className: "errorToast",
      });
      return;
    }

    if (
      item?.appointment_details?.video_platform === "external"
    ) {
      try {
        const response = await Api.interpreterJoinExternalVideoCall(id);

        if (response?.status >= 200 && response?.status < 300) {
        const link = item.appointment_details.video_link;

        const url = link.startsWith('http://') || link.startsWith('https://')
          ? link
          : `https://${link}`;

        window.open(url, "_blank");
        } else {
          const errorMsg = response?.data?.error || response?.data?.message || t("appointments.failedToJoinExternalCall");
          toast.error(errorMsg, { className: "errorToast" });
        }
      } catch (err) {
        const serverError = err?.response?.data?.error || err?.message || t("appointments.errorJoiningVideoCall");
        toast.error(serverError, { className: "errorToast" });
        console.error("Error joining external video call:", err);
      }
      return;
    }

    try {
      const response = await Api.interpreterjoinVideoCall(id);
      if (response?.status === 200) {
        const iframeUrl = response?.data?.data?.iframe_url;
        dispatch(setInterpreterIframeUrl(iframeUrl));
        navigate(`join-room-call`);
        // console.log("Joined successfully:", response);
      } else {
        const errorMsg =
          response?.data?.statusText ||
          response?.data?.message ||
          response?.data?.error;
        console.error("Failed to join:", errorMsg);
        toast.error(errorMsg, { className: "errorToast" });
      }
    } catch (err) {
      console.error("Error joining video call:", err);
    }
  };

  //   Show recording popup
  const showRecordingPopup = () => {
    if (item?.appointment_details?.recording) {
      setRecordingUrl(item?.appointment_details?.recording);
      setShowModal(true);
    } else {
      toast.error(t("appointmentView.recordingUnavailable"), {
        className: "errorToast",
      });
    }
  };

  const inviteStatus = item?.invite_details?.status;

  const notDone = item?.status !== "cancelled" && item?.status !== "completed";
  const isVri = item?.type === "Video Remote Interpretation";
  const dynamicRows = dynamicFieldRows(item, t);
  const workOrder = item?.type === "In Person" ? workOrderRows(item, t) : [];
  const standbyForLink = isVri && item?.status === "assigned" && item?.appointment_details?.video_platform === "external" && !item?.appointment_details?.video_link;

  const whenRows = [
    { label: t("table.date"), value: formattedDate },
    { label: t("appointmentView.yourTime"), value: formattedClientTime },
  ];

  const standbyBanner = standbyForLink ? (
    <div className="rounded d-flex justify-content-center align-items-center mt-3" style={{ backgroundColor: "#fcc10052" }}>
      <div className="standby-banner text-dark" style={{ fontWeight: 600, padding: "6px 12px", fontSize: "16px" }}>
        {t("appointments.standByForVideoLink")}
      </div>
    </div>
  ) : null;

  return (
    <div className="content appt-view">
      {loading && <FullPageLoader />}

      <AppointmentViewHeader item={item} banner={standbyBanner} />

      <ActionBar
        right={
          <>
            {item?.appointment_details?.recording && (
              <SubmitButton buttonType="button" className="Cancle_button" btnText={t("appointments.viewRecording")} handleSubmit={showRecordingPopup} />
            )}
            {isVri && item?.status === "assigned" && (item?.appointment_details?.video_platform === "algovi" || (item?.appointment_details?.video_platform === "external" && !!item?.appointment_details?.video_link)) && (
              <SubmitButton buttonType="button" className="green" btnText={t("appointments.joinVideoCall")} icon={<Video />} handleSubmit={handleInterpreterJoinVideoCall} />
            )}
          </>
        }
      >
        {item?.status === "available" && inviteStatus !== "accepted" && (
          <SubmitButton buttonType="button" className="green" btnText={t("appointments.imAvailable")} handleSubmit={() => setAnswerInviteModal("accept")} />
        )}
        {item?.status === "available" && inviteStatus !== "accepted" && (
          <SubmitButton buttonType="button" className="table_delete" btnText={t("appointments.decline")} handleSubmit={() => setAnswerInviteModal("decline")} />
        )}
        {item?.status === "available" && inviteStatus === "accepted" && (
          <div className="d-flex align-items-center" style={{ color: "green", fontWeight: 500 }}>
            {t("appointments.inviteAcceptedSuccess")}
          </div>
        )}
        {notDone && item?.status !== "available" && (
          <SubmitButton buttonType="button" className="tomatoRed" btnText={t("common.cancel")} handleSubmit={handleCancel} />
        )}
        {notDone && item?.status !== "available" && !standbyForLink && (
          <SubmitButton buttonType="button" className="Cancle_button" btnText={t("appointments.checkinCheckout")} handleSubmit={handleOpenCheckinCheckoutModal} />
        )}
      </ActionBar>

      <div className="appt-grid">
        <div className="appt-col">
          <DetailCard title={t("appointmentView.cards.details")} rows={appointmentRows(item, t, { when: whenRows, showClient: item?.status !== "available" })} />
        </div>
        <div className="appt-col">
          <DetailCard title={t("appointmentView.cards.location")} rows={locationRows(item, t)} />
          <DetailCard title={t("appointmentView.cards.requester")} rows={requesterRows(item, t)} />
          <DetailCard title={t("appointmentView.cards.requirements")} rows={requirementRows(item, t)} />
        </div>
        <DetailCard className="appt-grid__full" title={t("appointmentView.cards.otherDetails")} columns={2} rows={otherDetailRows(item, t, { adminNotes: false })} />
        {dynamicRows.length > 0 && (
          <DetailCard className="appt-grid__full" title={t("appointmentView.cards.customFields")} columns={2} rows={dynamicRows} />
        )}
        <DetailCard className="appt-grid__full" title={t("appointmentView.cards.checkInOut")} columns={2} rows={checkInOutRows(item, t)} />
        {isVri && <DetailCard title={t("appointmentView.cards.video")} rows={videoRows(item, t)} />}
        {workOrder.length > 0 && (
          <DetailCard className="appt-grid__full" title={t("appointmentView.cards.workOrder")} columns={2} rows={workOrder} />
        )}
        {item?.status === "completed" && item?.appointment_details?.feedback_mail_sent == 1 && item?.feedback && (
          <DetailCard className="appt-grid__full" title={t("appointmentView.cards.feedback")} rows={feedbackRows(item, t)} />
        )}
      </div>

      <CheckinCheckoutAdjustmentModal
        isOpen={checkinCheckoutModal}
        handleClose={handleCloseCheckinCheckoutModal}
        onConfirm={handleSubmit}
        appointmentId={id}
        onSubmit={handleSubmit}
        item={item}
        loader={loading}
        targetTimezone={InterpreterTimezone?.name}
      />
      <CancelModal
        isOpen={cancelModal}
        onClose={handleCloseCancel}
        onConfirm={handleSubmit}
        appointmentId={id}
        item={item}
        loader={loading}
        end_point="interpreter-cancel"
      />
      <RecordingModal
        recordingUrl={recordingUrl}
        showModal={showModal}
        onClose={() => setShowModal(false)}
      />
      <ConfirmationInvitesModal
        isOpen={answerInviteModal}
        onClose={() => setAnswerInviteModal('')}
        onConfirm={(ans) => handleAnswerInvite(ans)}
        processing={answerProcessing}
      />
    </div>
  );
};

export default AppointmentView;
